// React Tools
import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// Custom Context
import { useLanguage } from '../../context/LanguageContext';
import { useEbdaEduAuth } from '../../context/EbdaEduAuthContext';
import { useTheme } from 'context/ThemeContext';
// Custom Components
import Popup from '../../components/Popup';
import SystemCard from '../../components/SystemCard';
import EbdaEduNavbar from 'components/navbars/EbdaEduNavbar';
// Constants
import { getEbdaEduSystems } from '../../constants/constants';
// Hooks
import { useSearchFilter } from '../../hooks/useSearchFilter';
// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faUser } from '@fortawesome/free-solid-svg-icons';

const WabysAdminMain = () => {
    const navigate = useNavigate();
    const { ebdaUserInfo } = useEbdaEduAuth();
    const { language } = useLanguage();
    const [notAvailable, setNotAvailable] = useState(false);
    const { darkMode } = useTheme();
    const systems = useMemo(
        () => getEbdaEduSystems(language, ebdaUserInfo?.organization_id),
        [language, ebdaUserInfo?.organization_id]
    );
    const getTitle = useCallback(system => system.title, []);
    const { filteredItems: filteredSystems } = useSearchFilter(systems, getTitle);

    const openPopup = () => {
        setNotAvailable(true);
    };

    return (
        <div className={`w-full min-h-screen font-[Cairo,sans-serif] transition-colors duration-500 ${darkMode ? 'bg-watomsBlue text-white' : 'bg-gradient-to-br from-blue-50 via-white to-purple-100 text-gray-900'} relative overflow-hidden`}>
            {/* Modern Background with Abstract Shapes */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {/* Animated Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100/30 via-purple-100/20 to-pink-100/30 animate-pulse" style={{ animationDuration: '8s' }} />


                {/* Grid Pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="w-full h-full" style={{
                        backgroundImage: `radial-gradient(circle at 1px 1px, ${darkMode ? 'white' : 'gray'} 1px, transparent 0)`,
                        backgroundSize: '40px 40px'
                    }} />
                </div>
            </div>

            {/* Navbar */}
            <EbdaEduNavbar>
                {/* User Profile Button */}
                <button
                    className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                    title={language ? 'My Profile' : 'الملف الشخصي'}
                    onClick={() => navigate('/admin/user-profile')}
                >
                    <FontAwesomeIcon icon={faUser} className="text-watomsBlue text-lg" />
                </button>
                {/* System Info */}
                <button
                    className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                    title={language ? 'System Info' : 'معلومات النظام'}
                    onClick={() => navigate('/admin/system-info')}
                >
                    <FontAwesomeIcon icon={faInfoCircle} className="text-watomsBlue text-lg" />
                </button>
            </EbdaEduNavbar>
            {/* Main Body */}
            <div className="relative z-10 px-6 pb-5">
                {/* Ebda Edu Systems Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-12 max-w-7xl mx-auto">
                    {filteredSystems.map(system => (
                        <SystemCard
                            key={system.id}
                            openPopup={() => openPopup()}
                            available={system.available}
                            path={system.path}
                            color={system.color}
                            iconType="awesome"
                            icon={system.icon}
                            title={system.title}
                            subtitle={system.subtitle}
                        />
                    ))}
                </div>
            </div>

            <Popup
                isOpen={notAvailable}
                onClose={() => setNotAvailable(false)}
                message={language ? "This system will be available soon" : "هذا النظام سيتم إطلاقه قريباً"}
                button={language ? "OK" : "حسناً"}
                form={false}
            />
        </div>
    );
};

export default WabysAdminMain;